import React, { ChangeEvent } from "react";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { ValidationIssue } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { FormControl } from "../FormControl";
import { PropertyEditorSelect } from "../../../CommonUI/PropertyEditing/PropertyEditorSelect";
import { PropertyEditorText } from "../../../CommonUI/PropertyEditing/PropertyEditorText";
import { validateVariableName } from "../../../rule-engine-core/Common/DataValidationUtilities";
import { TextField } from "@mui/material";
import { ObjectDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { NumberDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/NumberDataDefinition";
import UIChart from "../../../CommonUI/BuildlingBlocks/UIChart";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { chartNonSeries } from "../Testing/ChartSameple";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { PropertyEditorCollapse } from "../../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

export enum ChartType {
    BarChart = "BarChart",
    LineChart = "LineChart",
}

export class LineChartFormControl extends FormControl {
    readonly __type: string = "Line Chart";

    chartType: string;
    objectPath?: string;
    dataName?: string;
    labelName?: string;
    valueName?: string;
    randomColor: boolean;
    label?: string;
    showLegend: boolean;
    legendPosition?: string;
    show3D: boolean;
    // tooltip: boolean;
    cursor: boolean;
    exportable: boolean;
    multiValue: boolean;
    stackValue: boolean;

    constructor() {
        super();
        this.chartType = ChartType.BarChart;
        this.label = "Configure Label";
        this.randomColor = true;
        this.showLegend = false;
        this.show3D = false;
        this.multiValue = false;
        this.stackValue = false;
        // this.tooltip = false;
        this.cursor = false;
        this.exportable = false;
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        if (!this.dataName) {
            issues.push(new ValidationIssue("Data name is required", undefined, this.id));
        } else {
            let varIssue = validateVariableName(this.dataName);

            if (varIssue) {
                varIssue.dataName = this.dataName;
                varIssue.elementId = this.id;
                issues.push(varIssue);
            }
        }

        return issues;
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        return [];
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        return undefined;
    }

    // updateColor() {
    //     const dataDef = this.getFormContext()?.form.getInputDefinition();
    //     const chartDataDef = dataDef?.getFieldByDataPath(this.dataName!) as ObjectDataDefinition;
    //     const fields = chartDataDef.fields;
    //     const colorDefIndex = fields.findIndex((field) => field.name === "color");
    //     if (colorDefIndex > -1 && this.randomColor) {
    //         fields.splice(colorDefIndex, 1);
    //     }
    //     if (colorDefIndex === -1 && !this.randomColor) {
    //         const colorDef = new StringDataDefinition("color");
    //         fields.push(colorDef);
    //     }
    //     this.controlUpdate();
    // }

    updateChartData(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const value = e.target.value;
        const dataDef = this.getFormContext()?.form.getInputDefinition();
        if (this.dataName) {
            const chartDataDef = dataDef?.getFieldByDataPath(this.dataName) as ObjectDataDefinition;
            chartDataDef.name = value;
            const fields = chartDataDef.fields;
            const colorDefIndex = fields.findIndex((field) => field.name === "color");
            if (colorDefIndex > -1 && this.randomColor) {
                fields.slice(colorDefIndex, 1);
            }
            if (colorDefIndex === -1 && !this.randomColor) {
                const colorDef = new StringDataDefinition("color");
                fields.push(colorDef);
            }
            Reflect.set(this, "dataName", value);
        } else {
            Reflect.set(this, "dataName", value);
            const chartDataDef = new ObjectDataDefinition(value);
            chartDataDef.list = true;
            const labelDef = new StringDataDefinition(this.labelName);
            const valueDef = new NumberDataDefinition(this.valueName);
            chartDataDef.fields.push(labelDef);
            chartDataDef.fields.push(valueDef);
            if (!this.randomColor) {
                const colorDef = new StringDataDefinition("color");
                chartDataDef.fields.push(colorDef);
            }
            dataDef?.fields.push(chartDataDef);
        }
        StateManager.changed(this);
    }

    getChartDefSampleValue(): any[] {
        const dataDef = this.getFormContext()?.form.getInputDefinition();
        if (!dataDef || !this.dataName) {
            return chartNonSeries;
        }
        const data = Reflect.get(dataDef.value, this.dataName) as any[];
        if (!data) return chartNonSeries;
        return data.map((dataItem) => {
            if (dataItem.color) {
                return {
                    label: dataItem.label,
                    value: dataItem.value,
                    color: dataItem.color,
                };
            } else {
                return {
                    label: dataItem.label,
                    value: dataItem.value,
                };
            }
        });
    }

    render() {
        return (
            <>
                {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                {this.getChartDefSampleValue() && this.getChartDefSampleValue().length > 0 && (
                    <div>
                        <UIChart
                            options={{
                                data: this.getChartDefSampleValue(),
                                category: this.labelName,
                                value: this.valueName,
                                chartType: this.chartType,
                                showLegend: this.showLegend,
                                legendPosition: this.legendPosition,
                                show3D: this.show3D,
                                // tooltip: this.tooltip,
                                cursor: this.cursor,
                                exportable: this.exportable,
                            }}
                        />
                    </div>
                )}
            </>
        );
    }

    renderEditUI(): JSX.Element | null {
        return (
            <UISpacedBoxContent>
                <PropertyEditorText
                    editObject={this}
                    label={"Label"}
                    propertyName="label"
                />

                <PropertyEditorSelect
                    options={Object.keys(ChartType)}
                    propertyName="chartType"
                    editObject={this}
                    label={"Chart Type"}
                />

                <TextField
                    label="Dataname"
                    value={this.dataName}
                    onChange={(e) => this.updateChartData(e)}
                    variant="outlined"
                />

                <PropertyEditorText
                    editObject={this}
                    label={"Value axis title"}
                    propertyName="valueName"
                />

                <PropertyEditorText
                    editObject={this}
                    label={"Label axis title"}
                    propertyName="labelName"
                />

                <PropertyEditorBoolean
                    label="Show Legend"
                    propertyName="showLegend"
                    editObject={this}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={"showLegend"}
                >
                    <PropertyEditorSelect
                        label="Legend Position"
                        options={["top", "bottom", "left", "right"]}
                        propertyName="legendPosition"
                        editObject={this}
                    />
                </PropertyEditorCollapse>

                <PropertyEditorBoolean
                    label="Show 3D"
                    propertyName="show3D"
                    editObject={this}
                />

                {/* <PropertyEditorBoolean
                    label="Show Tooltip"
                    propertyName="tooltip"
                    editObject={this}
                    updateCallback={this.controlUpdate}
                /> */}

                <PropertyEditorBoolean
                    label="Show Cursor"
                    propertyName="cursor"
                    editObject={this}
                />

                <PropertyEditorBoolean
                    label="Exportable"
                    propertyName="exportable"
                    editObject={this}
                />
            </UISpacedBoxContent>
        );
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "__type",
            "chartType",
            "objectPath",
            "dataName",
            "labelName",
            "valueName",
            "randomColor",
            "label",
            "showLegend",
            "legendPosition",
            "show3D",
            "cursor",
            "exportable",
            "multiValue",
            "stackValue",
        );

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "__type",
            "chartType",
            "objectPath",
            "dataName",
            "labelName",
            "valueName",
            "randomColor",
            "label",
            "showLegend",
            "legendPosition",
            "show3D",
            "cursor",
            "exportable",
            "multiValue",
            "stackValue",
        );
    }
}
